import { forwardRef, useImperativeHandle } from "react";
import { ModifyResidentRef } from "../interfaces";
import { View } from "ui/atoms/view";
import KeyValueSelector from "ui/organisms/KeyValueSelector";
import { useHandleResidentDietaryOptions } from "../../hooks/useHandleDietaryOptions";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { TextAreaInput } from "ui/atoms/input/TextArea";
import { Text } from "react-native";
import { Controller } from "react-hook-form";

const ResidentDietFormTemplate = forwardRef<ModifyResidentRef, {}>((_, ref) => {
  const {
    control,
    solidFoodOptions,
    liquidFoodOptions,
    requiresDescriptionOptions,
    areCategoriesLoading,
    submitForm,
    errorMessage,
  } = useHandleResidentDietaryOptions();

  useImperativeHandle(
    ref,
    () => ({
      continue: () => submitForm(),
    }),
    [submitForm],
  );

  if (areCategoriesLoading) {
    return (
      <View className="flex-1 justify-center items-center my-10">
        <LoadingSpinner />
      </View>
    );
  }

  return (
    <View>
      <Controller
        control={control}
        name="selectedSolidFood"
        render={({ field: { value, onChange } }) => (
          <KeyValueSelector<number>
            title="Solid Food"
            options={solidFoodOptions}
            isMultiSelect={true}
            selectedValues={value}
            onChange={onChange}
          />
        )}
      />

      <View className="mt-5">
        <Controller
          control={control}
          name="selectedLiquidFood"
          render={({ field: { value, onChange } }) => (
            <KeyValueSelector<number>
              title="Liquid Food"
              options={liquidFoodOptions}
              isMultiSelect={false}
              selectedValues={value}
              onChange={onChange}
            />
          )}
        />
      </View>
      <View className="border border-gray-200 w-full my-12" />
      {requiresDescriptionOptions.map((option) => (
        <View key={option.value}>
          <Text className="mb-5 font-medium">Diet Allergies</Text>
          <Controller
            control={control}
            name="selectedRequiresDescription"
            render={({ field: { value, onChange } }) => {
              const existingItem = value.find(
                (item) => item.diet_category_id === option.value,
              ) || {
                diet_category_id: option.value,
                description: "",
              };

              return (
                <TextAreaInput
                  id="selectedRequiresDescription"
                  name="selectedRequiresDescription"
                  placeholder="Enter any diet allergies"
                  value={existingItem.description as string}
                  onChange={(text) => {
                    const updatedValue = value.map((item) =>
                      item.diet_category_id === option.value
                        ? { ...item, description: text }
                        : item,
                    );

                    if (
                      !value.some(
                        (item) => item.diet_category_id === option.value,
                      )
                    ) {
                      updatedValue.push({
                        diet_category_id: option.value,
                        description: text,
                      });
                    }

                    onChange(updatedValue);
                  }}
                />
              );
            }}
          />
        </View>
      ))}
      {errorMessage ? (
        <Text className="text-red-700">{errorMessage}</Text>
      ) : null}
    </View>
  );
});

ResidentDietFormTemplate.displayName = "ResidentDietFormTemplate";
export { ResidentDietFormTemplate };
